import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";

const SLIDES = [
  {
    eyebrow: "HLM Moving — Ashburn, Virginia",
    line1: "ORGANIZATION",
    line2: "IN MOTION",
    subtitle: "Keeping teams, spaces, and equipment moving forward.",
    tagline: "HLM Moving delivers office and facility relocation, workplace solutions, and employee mobility from our Ashburn, Virginia headquarters.",
    image: "/images/hero-1.png",
  },
  {
    eyebrow: "Planning to Placement",
    line1: "WORK SPACE",
    line2: "SOLUTIONS",
    subtitle: "Planning, program management, and asset services for every transition.",
    tagline: "HLM Moving delivers office and facility relocation, workplace solutions, and employee mobility from our Ashburn, Virginia headquarters.",
    image: "/images/hero-2.png",
  },
  {
    eyebrow: "People-First Relocation",
    line1: "EMPLOYEE",
    line2: "MOBILITY",
    subtitle: "Supporting relocating employees from first box to final placement.",
    tagline: "HLM Moving delivers office and facility relocation, workplace solutions, and employee mobility from our Ashburn, Virginia headquarters.",
    image: "/images/hero-3.png",
  },
];

const MaskedLine = ({ text, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      exit={{ y: "-110%" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {text}
    </motion.span>
  </span>
);

export default function HeroSlideshow() {
  const [index, setIndex] = useState(0);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 140]);

  const go = useCallback((dir) => {
    setIndex((i) => (i + dir + SLIDES.length) % SLIDES.length);
  }, []);

  useEffect(() => {
    const t = setInterval(() => go(1), 6500);
    return () => clearInterval(t);
  }, [go, index]);

  const slide = SLIDES[index];

  return (
    <section data-testid="hero-slideshow-container" className="relative h-[92vh] min-h-[620px] overflow-hidden bg-navy">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <AnimatePresence mode="sync">
          <motion.img
            key={slide.image}
            src={slide.image}
            alt={slide.subtitle}
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/40 to-navy/90" />
      </motion.div>

      <div className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-center">
        <AnimatePresence mode="wait">
          <motion.div key={index} className="max-w-3xl">
            <motion.p
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-xs uppercase tracking-[0.3em] font-semibold text-brand mb-6"
            >
              {slide.eyebrow}
            </motion.p>
            <h1 className="font-display font-bold uppercase tracking-tight leading-[1.02] text-5xl sm:text-6xl lg:text-7xl text-white">
              <MaskedLine text={slide.line1} delay={0.15} />
              <span className="text-brand">
                <MaskedLine text={slide.line2} delay={0.3} />
              </span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="mt-6 text-lg md:text-xl text-white font-medium"
            >
              {slide.subtitle}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-3 text-sm text-white/60 max-w-xl leading-relaxed"
            >
              {slide.tagline}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.85 }}
              className="mt-9 flex flex-wrap gap-4"
            >
              <Link
                to="/workplace-solutions"
                data-testid="hero-cta-explore-services"
                className="inline-flex items-center gap-2 border border-white/40 text-white hover:bg-white hover:text-navy text-sm font-bold uppercase tracking-wide px-7 py-3.5 rounded-sm transition-colors"
              >
                Explore Services
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-get-quote"
                className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white text-sm font-bold uppercase tracking-wide px-7 py-3.5 rounded-sm transition-colors"
              >
                Get a Quote <ArrowRight size={16} />
              </Link>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="absolute bottom-8 left-0 right-0 z-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
          <div className="flex gap-2">
            {SLIDES.map((s, i) => (
              <button
                key={i}
                data-testid={`hero-slide-indicator-${i}`}
                onClick={() => setIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-1 rounded-full transition-all duration-500 ${
                  i === index ? "w-12 bg-brand" : "w-6 bg-white/30 hover:bg-white/60"
                }`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <button
              data-testid="hero-slide-prev-btn"
              onClick={() => go(-1)}
              aria-label="Previous slide"
              className="w-11 h-11 border border-white/30 text-white flex items-center justify-center rounded-sm hover:bg-brand hover:border-brand transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              data-testid="hero-slide-next-btn"
              onClick={() => go(1)}
              aria-label="Next slide"
              className="w-11 h-11 border border-white/30 text-white flex items-center justify-center rounded-sm hover:bg-brand hover:border-brand transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
